import React from 'react';
import { Share2, BookOpen } from 'lucide-react';
import { Artifact, ViewType } from '../types';

interface SummaryViewProps {
  artifacts: Artifact[];
  onNavigate: (view: ViewType) => void;
  onShowToast: (msg: string) => void;
}

export const SummaryView: React.FC<SummaryViewProps> = ({
  artifacts,
  onNavigate,
  onShowToast,
}) => {
  const unlocked = artifacts.filter((a) => a.unlocked);
  const percent = Math.round((unlocked.length / artifacts.length) * 100);

  const handleShare = () => {
    onShowToast('Đã tạo thẻ chia sẻ mẫu hành trình VNFAM 2.0 thành công!');
  };

  return (
    <div className="summary-stage min-h-[80vh] flex items-center justify-center px-4 sm:px-6 pb-20 animate-fade-in">
      <div className="max-w-4xl w-full">
        <article className="postcard">
          {/* Postcard Header */}
          <header className="postcard-header">
            <div className="max-w-xl space-y-2 relative z-10">
              <span className="text-xs font-bold text-[#e6ba55] uppercase tracking-widest block">
                CHUYẾN KHÁM PHÁ CỦA AN · BẢN GHI DẤU
              </span>
              <h1 className="text-3xl sm:text-5xl font-serif font-black leading-tight text-white">
                Mỗi tác phẩm, <br />
                <em className="text-[#e6ba55] not-italic">một dấu ấn để nhớ.</em>
              </h1>
              <p className="text-xs sm:text-sm text-[#fff4dd]/80 pt-2">
                Thành quả khám phá văn hóa và mỹ thuật được ghi nhận độc lập qua từng trò chơi và chuyến đi thực tế.
              </p>
            </div>
          </header>

          {/* Postcard Body */}
          <div className="summary-body">
            {/* Big Progress Counter */}
            <div className="big-progress">
              <div className="flex items-center gap-4">
                <span className="progress-number">
                  {unlocked.length}/{artifacts.length}
                </span>
                <div>
                  <h2 className="text-lg font-serif font-bold text-white">
                    Tem Quốc bảo đã sưu tập
                  </h2>
                  <p className="text-xs text-[#cdbdaf] mt-0.5">
                    Đạt {percent}% tiến trình Bộ Quốc bảo trên tài khoản
                  </p>
                </div>
              </div>

              <div className="w-full sm:w-48 h-2.5 bg-[#171013] rounded-full overflow-hidden border border-[#765d55]">
                <div
                  className="h-full bg-gradient-to-r from-[#b80735] to-[#e6ba55] transition-all duration-500 rounded-full"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            {/* Mini Stamps Gallery */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <strong className="text-[#e6ba55] font-bold uppercase tracking-wider">
                  CÁC CON TEM ĐÃ MỞ KHÓA ({unlocked.length})
                </strong>
                <span className="text-[#cdbdaf]">Chạm để xem trong Bộ sưu tập</span>
              </div>

              <div className="flex items-center gap-3 overflow-x-auto py-2">
                {unlocked.map((art) => (
                  <div
                    key={art.id}
                    onClick={() => onNavigate('journey')}
                    className="w-24 sm:w-28 aspect-[4/5] shrink-0 bg-[#2c2025] p-2 rounded-xl border border-[#765d55] shadow-lg cursor-pointer hover:border-[#e6ba55] hover:scale-105 transition-all"
                  >
                    <img
                      src={art.stamp}
                      alt={art.title}
                      className="w-full h-full object-contain filter drop-shadow"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 3 Games Achievements */}
            <div className="space-y-3">
              <strong className="text-xs text-[#e6ba55] font-bold uppercase tracking-wider block">
                THÀNH TÍCH 03 TRÒ CHƠI
              </strong>

              <div className="achievements-grid">
                <div className="achievement">
                  <span className="text-[10px] font-bold text-[#e6ba55] uppercase tracking-wider block">
                    LEO THÁP · THƯỜNG
                  </span>
                  <strong>Tầng cao nhất 07</strong>
                  <small>31 câu đúng</small>
                </div>

                <div className="achievement">
                  <span className="text-[10px] font-bold text-[#f0a07c] uppercase tracking-wider block">
                    VẼ VÀ ĐOÁN
                  </span>
                  <strong>1.260 điểm</strong>
                  <small>Hạng 31 toàn tuần</small>
                </div>

                <div className="achievement">
                  <span className="text-[10px] font-bold text-[#a0c0f0] uppercase tracking-wider block">
                    GHÉP TRANH
                  </span>
                  <strong>04 tranh hoàn tất</strong>
                  <small>Thời gian tốt nhất 01:15</small>
                </div>
              </div>
            </div>

            {/* Action CTAs */}
            <div className="pt-4 flex flex-wrap items-center gap-3 border-t border-[#765d55]/60">
              <button
                onClick={handleShare}
                className="gf-button text-sm py-2.5 px-6 shadow-md flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                <span>Tạo thẻ chia sẻ</span>
              </button>

              <button
                onClick={() => onNavigate('journey')}
                className="gf-button gf-button-secondary text-sm py-2.5 px-6 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                <span>Xem lại Bộ sưu tập</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};
